import { ApolloClient, InMemoryCache, HttpLink, NormalizedCacheObject } from '@apollo/client';
import logger from '../utils/logger';

// Define types for deployment details
interface SubgraphDetails {
    name: string;
    url: string;
    contractAddress: string;
}

interface DeploymentDetails {
    subgraphs?: Record<string, SubgraphDetails>;
    core?: Record<string, any>;
    // Add other properties as needed
}

// Function to get deployment details based on chain ID
const getDeploymentDetails = async (chainId: string = '8453'): Promise<DeploymentDetails | null> => {
    try {
        // In a production environment, this would be imported from a generated file
        // For now, we'll try to fetch it dynamically
        return fetch(`/deployments/deployment-details-${chainId}.json`)
            .then(response => response.json())
            .catch(() => {
                console.warn(`Failed to load deployment details for chain ${chainId}, using default subgraph URLs`);
                return null;
            });
    } catch (error) {
        console.warn(`Error loading deployment details: ${error}`);
        return null;
    }
};

// Track logging to avoid spam
let graphNodeUrlLogged = false;
let envGraphNodeUrlLogged = false;

// Get the Graph Node URL from environment variables or use default
const getGraphNodeBaseUrl = (chainId: string): string => {
    // Early return if subgraphs are disabled
    if (import.meta.env.VITE_DISABLE_SUBGRAPHS === 'true') {
        logger.info('🚫 getGraphNodeBaseUrl (v2): Subgraphs disabled, returning empty URL');
        return '';
    }

    // First check if we have a global override variable
    if (typeof window !== 'undefined' && (window as any).__GRAPH_NODE_URL__) {
        // Reduced logging - only log once per session
        if (!graphNodeUrlLogged) {
            logger.debug('Using Graph Node URL from window.__GRAPH_NODE_URL__:', (window as any).__GRAPH_NODE_URL__);
            graphNodeUrlLogged = true;
        }
        return (window as any).__GRAPH_NODE_URL__;
    }

    // Then check environment variable if available
    const envGraphNodeUrl = import.meta.env.VITE_GRAPH_NODE_URL;
    if (envGraphNodeUrl) {
        // Only log environment URL once
        if (!envGraphNodeUrlLogged) {
            logger.debug('Using Graph Node URL from env:', envGraphNodeUrl);
            envGraphNodeUrlLogged = true;
        }
        return envGraphNodeUrl;
    }

    // Fallback for local development (Anvil) - no hardcoded ngrok URL
    if (chainId === '31337') {
        logger.warn('⚠️ No Graph Node URL configured for Anvil chain 31337, using localhost:8000');
        return 'http://localhost:8000';
    }

    // For mainnet chains, use Alchemy subgraphs
    return 'https://subgraphs.alchemy.com/api/subgraphs';
};

// Track URL generation to avoid spam
let urlsGenerated = false;

// Default subgraph URLs for different environments
const getDefaultSubgraphUrls = (chainId: string): Record<string, string> => {
    // Early return if subgraphs are disabled
    if (import.meta.env.VITE_DISABLE_SUBGRAPHS === 'true') {
        return {};
    }

    const baseUrl = getGraphNodeBaseUrl(chainId);

    // Only log URL generation once per session
    if (!urlsGenerated) {
        console.log(`Using base URL for subgraphs: ${baseUrl}`);
        urlsGenerated = true;
    }

    if (chainId === '31337') {
        // For ngrok or local development
        const urls = {
            timelockTracker: `${baseUrl}/subgraphs/name/memer-multisigtimelockcontroller-31337`,
            coreTracker: `${baseUrl}/subgraphs/name/memer-memercore-31337`,
            featuresTracker: `${baseUrl}/subgraphs/name/memer-memerfeatures-31337`,
            controllerTracker: `${baseUrl}/subgraphs/name/memer-memercontroller-31337`
        };
        return urls;
    } else if (chainId === '8453') {
        return {
            timelockTracker: `${baseUrl}/name/memer-timelockcontroller-8453`,
            coreTracker: `${baseUrl}/name/memer-memercore-8453`,
            featuresTracker: `${baseUrl}/name/memer-memerfeatures-8453`,
            controllerTracker: `${baseUrl}/name/memer-memercontroller-8453`
        };
    } else if (chainId === '84532') {
        return {
            timelockTracker: `${baseUrl}/name/memer-timelockcontroller-84532`,
            coreTracker: `${baseUrl}/name/memer-memercore-84532`,
            featuresTracker: `${baseUrl}/name/memer-memerfeatures-84532`,
            controllerTracker: `${baseUrl}/name/memer-memercontroller-84532`
        };
    }

    // Default to local development
    return {
        timelockTracker: `${baseUrl}/subgraphs/name/memer-multisigtimelockcontroller-31337`,
        coreTracker: `${baseUrl}/subgraphs/name/memer-memercore-31337`,
        featuresTracker: `${baseUrl}/subgraphs/name/memer-memerfeatures-31337`,
        controllerTracker: `${baseUrl}/subgraphs/name/memer-memercontroller-31337`
    };
};

// Default subgraph URLs for different environments (kept for backward compatibility)
// Only initialize if subgraphs are enabled
const DEFAULT_SUBGRAPH_URLS: Record<string, Record<string, string>> = {};

// Lazy initialization function for default URLs
const getDefaultSubgraphUrlsForChain = (chainId: string): Record<string, string> => {
    if (import.meta.env.VITE_DISABLE_SUBGRAPHS === 'true') {
        console.log('🚫 getDefaultSubgraphUrlsForChain: Subgraphs disabled, returning empty URLs');
        return {};
    }

    if (!DEFAULT_SUBGRAPH_URLS[chainId]) {
        if (chainId === '31337') {
            DEFAULT_SUBGRAPH_URLS[chainId] = getDefaultSubgraphUrls('31337');
        } else if (chainId === '8453') {
            DEFAULT_SUBGRAPH_URLS[chainId] = {
                timelockTracker: 'https://subgraphs.alchemy.com/api/subgraphs/name/memer-timelockcontroller-8453',
                coreTracker: 'https://subgraphs.alchemy.com/api/subgraphs/name/memer-memercore-8453',
                featuresTracker: 'https://subgraphs.alchemy.com/api/subgraphs/name/memer-memerfeatures-8453',
                controllerTracker: 'https://subgraphs.alchemy.com/api/subgraphs/name/memer-memercontroller-8453'
            };
        } else if (chainId === '84532') {
            DEFAULT_SUBGRAPH_URLS[chainId] = {
                timelockTracker: 'https://subgraphs.alchemy.com/api/subgraphs/name/memer-timelockcontroller-84532',
                coreTracker: 'https://subgraphs.alchemy.com/api/subgraphs/name/memer-memercore-84532',
                featuresTracker: 'https://subgraphs.alchemy.com/api/subgraphs/name/memer-memerfeatures-84532',
                controllerTracker: 'https://subgraphs.alchemy.com/api/subgraphs/name/memer-memercontroller-84532'
            };
        }
    }

    return DEFAULT_SUBGRAPH_URLS[chainId] || {};
};

// Create and export Apollo clients for different subgraphs
export const createApolloClients = async (chainId: string = '31337') => {
    // Check if subgraphs are disabled via environment variable
    if (import.meta.env.VITE_DISABLE_SUBGRAPHS === 'true') {
        console.log('🚫 Subgraphs disabled via VITE_DISABLE_SUBGRAPHS environment variable');
        console.log('📋 Returning empty clients object - no subgraph connections will be made');
        return {};
    }

    console.log(`Creating Apollo clients for chain ID: ${chainId}`);

    // Try to get deployment details
    const deploymentDetails = await getDeploymentDetails(chainId);
    console.log('Deployment details loaded:', deploymentDetails ? 'Yes' : 'No');

    // Initialize clients object
    const clients: Record<string, ApolloClient<NormalizedCacheObject>> = {};

    // If deployment details are available, use them to create clients
    if (deploymentDetails?.subgraphs) {
        console.log('Using subgraph URLs from deployment details');

        // Map subgraph keys from deployment details to client keys expected by the context
        const subgraphKeyMap: Record<string, string> = {
            multisigtimelockTracker: 'timelockTracker',
            memercore: 'coreTracker',
            memerfeatures: 'featuresTracker',
            memerTracker: 'controllerTracker',
            // Add mappings for other subgraphs if needed
            uniswapPoolsTracker: 'uniswapPoolsTracker',
            sushiswapPoolsTracker: 'sushiswapPoolsTracker',
            aerodromePoolsTracker: 'aerodromePoolsTracker'
        };

        // Create a client for each subgraph in the deployment details
        for (const [key, details] of Object.entries(deploymentDetails.subgraphs)) {
            let subgraphUrl = details.url;
            const clientKey = subgraphKeyMap[key] || key; // Use mapped key or original key

            // If we have an environment variable for the graph node URL, use it to override
            // the URL in the deployment details
            if (import.meta.env.VITE_GRAPH_NODE_URL && subgraphUrl.includes('localhost:8000')) {
                const baseUrl = import.meta.env.VITE_GRAPH_NODE_URL;
                const path = subgraphUrl.split('localhost:8000')[1];
                subgraphUrl = `${baseUrl}${path}`;
                console.log(`Overriding URL for ${clientKey} with env var: ${subgraphUrl}`);
            }

            if (subgraphUrl) {
                console.log(`Creating Apollo client for ${clientKey} (from ${key}) with URL: ${subgraphUrl}`);

                clients[clientKey] = new ApolloClient({
                    link: new HttpLink({ uri: subgraphUrl }),
                    cache: new InMemoryCache(),
                    defaultOptions: {
                        watchQuery: {
                            fetchPolicy: 'network-only',
                            nextFetchPolicy: 'cache-first',
                        },
                    },
                });
            }
        }

        // Log available clients for debugging
        console.log('Available Apollo clients:', Object.keys(clients));
    }

    // If no clients were created from deployment details, use defaults
    if (Object.keys(clients).length === 0) {
        console.log('No clients created from deployment details, using defaults');

        // Use the dynamic function to get URLs based on environment
        const defaultUrls = getDefaultSubgraphUrls(chainId);

        for (const [key, url] of Object.entries(defaultUrls)) {
            console.log(`Creating default Apollo client for ${key} with URL: ${url}`);

            clients[key] = new ApolloClient({
                link: new HttpLink({ uri: url }),
                cache: new InMemoryCache(),
                defaultOptions: {
                    watchQuery: {
                        fetchPolicy: 'network-only',
                        nextFetchPolicy: 'cache-first',
                    },
                },
            });
        }
    }

    return clients;
};

// Create default clients for immediate use
// These will be replaced once we have the chain ID
let defaultClients: Record<string, ApolloClient<NormalizedCacheObject>> = {};

// Lazy initialization function for default clients
const getDefaultClients = (): Record<string, ApolloClient<NormalizedCacheObject>> => {
    if (import.meta.env.VITE_DISABLE_SUBGRAPHS === 'true') {
        console.log('🚫 Default clients: Subgraphs disabled via VITE_DISABLE_SUBGRAPHS');
        return {};
    }

    if (Object.keys(defaultClients).length === 0) {
        // Create default clients for each subgraph type
        const defaultUrls = getDefaultSubgraphUrls('31337');
        for (const [key, url] of Object.entries(defaultUrls)) {
            defaultClients[key] = new ApolloClient({
                link: new HttpLink({ uri: url }),
                cache: new InMemoryCache()
            });
        }
    }

    return defaultClients;
};

// Only export default clients if subgraphs are enabled
export default import.meta.env.VITE_DISABLE_SUBGRAPHS === 'true'
    ? {}
    : getDefaultClients();
